// SPDX-License-Identifier: Elastic-2.0
/**
 * SettingsPage — the `/settings` route body (T-028).
 *
 * Composes five sections stacked vertically with a consistent
 * card-on-card layout per functional spec § 7. Each section is a
 * self-contained component that owns its own data + mutations; this
 * page is intentionally thin (composition only).
 *
 * Layout:
 *   - `<Page>` centers the column at ~720px, mirroring the dashboard
 *     column width.
 *   - Sections render in this order (matches the functional spec):
 *     Language · Theme · Privacy · Notifications · Paired devices · About.
 *
 * Account settings have moved to the standalone `/account` page in
 * F-331; `AccountSection` is mounted there instead.
 *
 * Children that hit TanStack Query (`useSettings`, `usePairedDevices`)
 * rely on the global `<QueryClientProvider>` mounted by `App` (already
 * in place — see `web/src/App.tsx`).
 */
import { useTranslation } from "react-i18next";

import { Page } from "@/components/Page/Page";

import { VoiceSettingsSection } from "@/features/voice/VoiceSettingsSection";

import { LanguageSection } from "./sections/LanguageSection";
import { ThemeSection } from "./sections/ThemeSection";
import { PrivacySection } from "./sections/PrivacySection";
import { NotificationsSection } from "./sections/NotificationsSection";
import { PairedDevicesSection } from "./sections/PairedDevicesSection";
import { ControllersStatusSection } from "./ControllersStatusSection";
import { AboutSection } from "./sections/AboutSection";
import { AiSection } from "./sections/AiSection";
import styles from "./SettingsPage.module.css";

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Page
      title={t("settings.page_title")}
      maxWidth={720}
      padding="md"
      data-testid="settings-page"
    >
      <div className={styles.column}>
        <LanguageSection />
        <ThemeSection />
        <VoiceSettingsSection />
        <AiSection />
        <PrivacySection />
        <NotificationsSection />
        <PairedDevicesSection />
        <ControllersStatusSection />
        <AboutSection />
      </div>
    </Page>
  );
}

export default SettingsPage;
